import type { FC, ReactNode } from 'react'

import { Fragment } from 'react'
import Image from 'next/image'
import { ArrowUpRight, Clock, Instagram } from 'lucide-react'

import { cn } from '@/lib/utils'
import { social } from '@/constants/site'
import { stats, turnaround, chatThread, clientAvatars } from '@/constants/content'
import { SplitText } from '@/components/motion/split-text'
import { Reveal } from '@/components/motion/reveal'
import { RevealImage } from '@/components/motion/reveal-image'
import { Counter } from '@/components/motion/counter'

/**
 * The autofocus reticle laid over the captioned photograph.
 *
 * Four corner brackets rather than a full square, which is what a camera
 * actually draws. It snaps inwards from an oversized frame as the card
 * arrives, so it reads as the lens locking on rather than as a decoration
 * fading in.
 */
const FocusMark: FC = (): ReactNode => (
    /* The centring lives on this outer span, not on the animated one. GSAP
       writes the element's `transform`, so anything positioned with a
       translate utility on the same node is at the mercy of how the two
       compose. Separating them keeps the placement fixed no matter what the
       tween is doing.

       It is aimed at the subject rather than at the middle of the card: cover
       scales that photograph by width at every breakpoint, so the face holds
       at roughly 60% across and a third of the way down whatever the card is
       doing, and the brackets can be pinned there. */
    <span
        aria-hidden='true'
        className='pointer-events-none absolute left-[60%] top-[32%] size-[6.5rem] -translate-x-1/2 -translate-y-1/2'
    >
        <Reveal y={0} scale={1.6} delay={0.45} duration={0.7} className='relative size-full'>
            <span className='absolute left-0 top-0 size-5 border-l-2 border-t-2 border-white' />
            <span className='absolute right-0 top-0 size-5 border-r-2 border-t-2 border-white' />
            <span className='absolute bottom-0 left-0 size-5 border-b-2 border-l-2 border-white' />
            <span className='absolute bottom-0 right-0 size-5 border-b-2 border-r-2 border-white' />
        </Reveal>
    </span>
)

interface StatProps {
    value: number
    suffix: string
    label: string
    className?: string
    delay?: number
}

/** A number with a raised unit, sitting above its caption. */
const Stat: FC<StatProps> = ({ value, suffix, label, className, delay = 0 }): ReactNode => (
    <Reveal
        delay={delay}
        y={28}
        className={cn('card-soft flex flex-col justify-between p-5', className)}
    >
        <p className='display-lg leading-none'>
            <Counter value={value} />

            <sup className='ml-0.5 align-super text-[0.42em] font-semibold text-[color:var(--gold-ink)]'>
                {suffix}
            </sup>
        </p>

        <p className='text-[0.75rem] leading-relaxed text-muted-foreground'>{label}</p>
    </Reveal>
)

export const WhyChoose: FC = (): ReactNode => {
    return (
        <section
            id='why-me'
            className='edge bg-background py-20 md:py-28'
            aria-labelledby='why-me-heading'
        >
            <div className='mx-auto max-w-[110rem]'>
                <div className='flex flex-col items-center text-center'>
                    <SplitText
                        as='h2'
                        lines={['Why choose me as your', 'Photographer?']}
                        className='display-xl'
                        lineClassName='mx-auto'
                    />

                    <span id='why-me-heading' className='sr-only'>
                        Why choose me
                    </span>

                    <Reveal delay={0.12}>
                        <p className='mt-6 max-w-md text-[0.9375rem] leading-relaxed text-muted-foreground'>
                            From expert guidance to a stress-free session, I am here to capture
                            your most memorable moments with quality and care.
                        </p>
                    </Reveal>
                </div>

                {/* Three masonry columns rather than one uniform grid, because
                    the cards in each column have different heights that have to
                    add up to the same total. Each column is its own flex stack,
                    so the tiling is exact at every width instead of depending on
                    row spans resolving the way a browser happens to pack them. */}
                <div className='mt-14 grid gap-3 sm:grid-cols-2 md:mt-18 lg:grid-cols-3'>
                    {/* Column one. */}
                    <div className='flex flex-col gap-3'>
                        <a
                            href={social[0].href}
                            target='_blank'
                            rel='noreferrer noopener'
                            className='group relative block h-[26rem] overflow-hidden rounded-[var(--radius-lg)] lg:h-[34.5rem]'
                        >
                            <RevealImage
                                src='/images/studio-loft.png'
                                alt='The Northlight studio mid-session, backdrop rolled down and prints pinned across the wall'
                                className='absolute inset-0'
                                imageClassName='transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]'
                                sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                                parallax={10}
                            />

                            <span
                                aria-hidden='true'
                                className='absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20'
                            />

                            <Instagram
                                aria-hidden='true'
                                className='absolute right-5 top-5 size-5 text-white/90'
                            />

                            <span className='absolute bottom-5 left-5 flex items-center gap-2 text-[1.0625rem] font-medium text-white'>
                                Check my instagram
                                <ArrowUpRight className='size-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1' />
                            </span>
                        </a>

                        <Reveal
                            delay={0.06}
                            y={28}
                            className='card-soft flex h-[9.5rem] items-center gap-5 p-6 lg:h-[10.5rem]'
                        >
                            <p className='display-lg shrink-0 leading-none'>
                                <Counter value={stats[2].value} />

                                <sup className='ml-0.5 align-super text-[0.42em] font-semibold text-[color:var(--gold-ink)]'>
                                    {stats[2].suffix}
                                </sup>
                            </p>

                            <p className='max-w-[7rem] text-[0.9375rem] leading-snug'>
                                Satisfied clients
                            </p>

                            <ul className='ml-auto flex shrink-0 items-center'>
                                {clientAvatars.map((avatar, index) => (
                                    <li
                                        key={avatar.src}
                                        className={cn(
                                            'relative size-11 overflow-hidden rounded-full ring-2 ring-[color:var(--card)]',
                                            index > 0 && '-ml-4'
                                        )}
                                        style={{ zIndex: clientAvatars.length - index }}
                                    >
                                        <Image
                                            src={avatar.src}
                                            alt={avatar.alt}
                                            fill
                                            sizes='44px'
                                            className='object-cover'
                                        />
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>

                    {/* Column two. */}
                    <div className='flex flex-col gap-3'>
                        <div className='relative h-[20rem] overflow-hidden rounded-[var(--radius-lg)] lg:h-[22.75rem]'>
                            {/* The box is always wider than the source is tall,
                                so cover scales by width and only the vertical
                                crop moves. Pinning it to 18% keeps the face in
                                the upper third at every breakpoint, which is
                                what the reticle below is aimed at. */}
                            <RevealImage
                                src='/images/sunhat-portrait.png'
                                alt='Portrait in full sun, the woven brim of a hat throwing dappled light across the face'
                                className='absolute inset-0'
                                imageClassName='object-[50%_18%]'
                                sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                                delay={0.05}
                                parallax={14}
                            />

                            <span
                                aria-hidden='true'
                                className='absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent'
                            />

                            <FocusMark />

                            <Reveal
                                delay={0.2}
                                y={24}
                                className='absolute inset-x-0 bottom-6 px-6'
                            >
                                <p className='text-center text-[1.0625rem] leading-snug text-white md:text-[1.1875rem]'>
                                    Enjoy high-resolution photos that capture every detail and
                                    emotion beautifully.
                                </p>
                            </Reveal>
                        </div>

                        <RevealImage
                            src='/images/shadow-lines.png'
                            alt='Black and white close study, blind slats laying bands of light across a face'
                            className='h-[16rem] rounded-[var(--radius-lg)] lg:h-[22.25rem]'
                            sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                            delay={0.1}
                            parallax={8}
                        />
                    </div>

                    {/* Column three. At the middle breakpoint it spans the full
                        width and lays its own cards out two-up, so it never
                        becomes a single tall ribbon beside the other columns. */}
                    <div className='grid gap-3 sm:col-span-2 sm:grid-cols-2 lg:col-span-1 lg:grid-cols-1'>
                        <div className='grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-1'>
                            <Stat
                                value={stats[1].value}
                                suffix={stats[1].suffix}
                                label='Clients book another session within a year'
                                className='h-[10rem] lg:h-[11rem]'
                            />

                            <div className='relative h-[10rem] overflow-hidden rounded-[var(--radius-lg)] bg-[color:var(--ink)] lg:h-[11rem]'>
                                {/* This source is already a low-key frame, so
                                    it needs none of the grayscale-and-crush
                                    treatment the old bright flat-lay did. Only
                                    a scrim under the label, leaving the warm
                                    highlight in the glass intact. */}
                                <RevealImage
                                    src='/images/camera-still.png'
                                    alt='A camera resting in raking afternoon light, the lens catching a warm highlight'
                                    className='absolute inset-0'
                                    imageClassName='object-[62%_60%] brightness-[0.92]'
                                    sizes='(max-width: 1024px) 25vw, 17vw'
                                    delay={0.08}
                                />

                                <span
                                    aria-hidden='true'
                                    className='absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0.72)_0%,rgba(0,0,0,0.28)_38%,transparent_70%)]'
                                />

                                <p className='absolute inset-x-0 top-4 text-center text-[0.75rem] text-white/85'>
                                    Taken with precision
                                </p>
                            </div>
                        </div>

                        {/* The enquiry thread, arriving message by message. */}
                        <Reveal
                            stagger={0.09}
                            y={18}
                            scale={0.92}
                            start='top 90%'
                            className='card-soft flex h-[21rem] flex-col justify-end gap-2 p-5 lg:h-[22.25rem]'
                        >
                            {chatThread.messages.map((message, index) => (
                                <Fragment key={index}>
                                    {message.stamped && (
                                        <p className='pt-2 text-center text-[0.6875rem] text-muted-foreground'>
                                            {chatThread.stamp}
                                        </p>
                                    )}

                                    <p
                                        className={cn(
                                            'max-w-[85%] rounded-[1.15rem] px-3.5 py-2 text-[0.8125rem] leading-snug',
                                            message.from === 'me'
                                                ? 'self-end bg-[color:var(--bubble-out)] text-white'
                                                : 'self-start bg-secondary text-foreground'
                                        )}
                                    >
                                        {message.text}
                                    </p>
                                </Fragment>
                            ))}
                        </Reveal>

                        <div className='relative h-[11rem] overflow-hidden rounded-[var(--radius-lg)]'>
                            <RevealImage
                                src='/images/cap-svc-lifestyle.jpg'
                                alt='A portrait session in open daylight'
                                className='absolute inset-0'
                                imageClassName='object-right'
                                sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                                delay={0.06}
                                parallax={10}
                            />

                            <span
                                aria-hidden='true'
                                className='absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent'
                            />

                            <Reveal delay={0.12} y={22} className='relative h-full p-6'>
                                {/* Capped below display-md so the label cannot
                                    wrap to two lines and push the blurb out of
                                    a fixed-height card at the middle widths. */}
                                <p className='display-md flex items-center gap-2 leading-none text-white [font-size:clamp(1.25rem,2.2vw,1.625rem)]'>
                                    <Clock className='size-5 shrink-0' aria-hidden='true' />
                                    {turnaround.label}
                                </p>

                                <p className='mt-3 max-w-[15rem] text-[0.8125rem] leading-relaxed text-white/80'>
                                    {turnaround.blurb}
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
